import React, { useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import { resolveMedia } from "@/lib/api";
import { CurrencyDollar, CheckCircle, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const METHODS = [
  { key: "zelle", name: "Zelle", color: "#6D1ED4", hint: "Send payment to" },
  { key: "venmo", name: "Venmo", color: "#008CFF", hint: "Pay this handle" },
];

export default function PayOptions() {
  const { settings } = useSettings();
  const payments = settings.payments || {};
  const [selected, setSelected] = useState("");

  const active = METHODS.filter(
    (m) => payments[m.key] && payments[m.key].enabled && (payments[m.key].label || payments[m.key].qr_url)
  );

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-20" data-testid="pay-options-page">
      <div className="flex items-center gap-3 mb-2">
        <CurrencyDollar size={32} weight="fill" className="text-[#FF5722]" />
        <h1 className="font-display text-4xl sm:text-5xl uppercase">Pay Your League Fee</h1>
      </div>
      <p className="text-white/60 mb-10">Choose how you'd like to pay. Please include your name (and team name) in the payment note so we can match your registration.</p>

      {payments.enabled && payments.instructions && (
        <div className="border border-white/10 bg-[#121212] p-5 mb-8" data-testid="pay-instructions">
          <p className="text-white/70 text-sm whitespace-pre-line">{payments.instructions}</p>
        </div>
      )}

      {active.length === 0 ? (
        <div className="border border-white/10 bg-[#121212] p-8 text-center" data-testid="pay-none">
          <p className="text-white/60">Online payment details aren't available right now. Our league admins will reach out with payment instructions shortly.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="pay-methods">
          {active.map((m) => {
            const info = payments[m.key];
            const isSel = selected === m.key;
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => setSelected(isSel ? "" : m.key)}
                data-testid={`pay-select-${m.key}`}
                aria-pressed={isSel}
                className={`text-left border p-6 transition-all duration-200 ${
                  isSel ? "border-[#FF5722] bg-[#FF5722]/5 shadow-[0_0_18px_rgba(255,87,34,0.25)]" : "border-white/15 bg-[#121212] hover:border-white/40"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1.5" style={{ background: m.color, color: "#fff" }}>{m.name}</span>
                  {isSel && <CheckCircle size={24} weight="fill" className="text-[#FF5722]" />}
                </div>
                {info.label && (
                  <div className="mb-4">
                    <div className="text-[10px] uppercase tracking-wide text-white/40 mb-1">{m.hint}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold break-all select-all" data-testid={`pay-label-${m.key}`}>{info.label}</span>
                      <span
                        onClick={(e) => { e.stopPropagation(); copy(info.label); }}
                        className="text-white/50 hover:text-[#FF5722] transition-colors shrink-0 cursor-pointer"
                        title="Copy"
                        data-testid={`pay-copy-${m.key}`}
                      >
                        <Copy size={18} />
                      </span>
                    </div>
                  </div>
                )}
                {info.qr_url && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-white/40 mb-2">Scan to pay</div>
                    <img src={resolveMedia(info.qr_url)} alt={`${m.name} QR code`} className="w-44 h-44 object-contain bg-white p-2" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <p className="text-white/40 text-sm mt-10">You can close this tab once your payment is sent. Your registration is already submitted — we'll confirm your spot after reviewing your payment.</p>
    </div>
  );
}
